import { defineStore } from 'pinia'

/**
 * Global User Store
 * 
 * This store manages user authentication state, permissions, and client access
 * throughout the application. It's automatically initialized with window.user data.
 * 
 * Usage in components:
 * ```javascript
 * import { useUserStore } from '@/stores/user'
 * 
 * export default {
 *   setup() {
 *     const userStore = useUserStore()
 *     return { userStore }
 *   }
 * }
 * ```
 * 
 * Available getters:
 * - userStore.isLoggedIn - Check if user is authenticated
 * - userStore.isAdmin - Check if user is admin
 * - userStore.getFullName - Get user's full name
 * - userStore.hasAccessToClient(clientId) - Check client access
 * 
 * Available actions:
 * - userStore.initialize(userData) - Initialize with user data
 * - userStore.setCurrentClient(client) - Set current client
 * - userStore.clearUser() - Clear user data (logout)
 * 
 * Note: For client-specific permissions, use the client store instead.
 */
export const useUserStore = defineStore('user', {
    state: () => ({
        user: null,
        isAuthenticated: false,
        permissions: [],
        clients: [],
        currentClient: null,
    }),
    
    getters: {
        // Get current user
        getCurrentUser: (state) => state.user,
        
        // Check if user is authenticated
        isLoggedIn: (state) => state.isAuthenticated,
        
        // Get user permissions
        getUserPermissions: (state) => state.permissions,
        
        // Get user's clients
        getUserClients: (state) => state.clients,
        
        // Get current client
        getCurrentClient: (state) => state.currentClient,
        
        // Check if user has specific permission
        hasPermission: (state) => (permission) => {
            return state.permissions.includes(permission);
        },
        
        // Check if user has access to a specific client
        hasAccessToClient: (state) => (clientId) => {
            return state.clients.some(client => client.id === clientId);
        },
        
        // Check if user is admin
        isAdmin: (state) => {
            return state.user?.user_type === 'admin';
        },
        
        // Check if user is agency admin
        isAgencyAdmin: (state) => {
            return state.user?.user_type === 'agency_admin';
        },
        
        // Get user's full name
        getFullName: (state) => {
            if (!state.user) return '';
            return `${state.user.first_name || ''} ${state.user.last_name || ''}`.trim() || state.user.name || '';
        },
        
        // Get user's initials
        getInitials: (state) => {
            if (!state.user) return '';
            const name = state.getFullName;
            return name.split(' ').map(n => n[0]).join('').toUpperCase();
        }
    },
    
    actions: {
        // Initialize user data
        initialize(userData) {
            this.user = userData;
            this.isAuthenticated = !!userData;
            this.permissions = userData?.permissions || [];
            this.clients = userData?.clients || [];
        },
        
        // Set current client
        setCurrentClient(client) {
            this.currentClient = client;
        },
        
        
        // Update user data
        updateUser(userData) {
            this.user = { ...this.user, ...userData };
        },
        
        // Add permission
        addPermission(permission) {
            if (!this.permissions.includes(permission)) {
                this.permissions.push(permission);
            }
        },
        
        // Remove permission
        removePermission(permission) {
            this.permissions = this.permissions.filter(p => p !== permission);
        },
        
        // Add client access
        addClientAccess(client) {
            const exists = this.clients.some(c => c.id === client.id);
            if (!exists) {
                this.clients.push(client);
            }
        },
        
        // Remove client access
        removeClientAccess(clientId) {
            this.clients = this.clients.filter(c => c.id !== clientId);
        },
        
        // Clear user data (logout)
        clearUser() {
            this.user = null;
            this.isAuthenticated = false;
            this.permissions = [];
            this.clients = [];
            this.currentClient = null;
        },
        
        // Refresh user data from server
        async refreshUser() {
            try {
                const response = await fetch('/api/user');
                if (response.ok) {
                    const userData = await response.json();
                    this.initialize(userData);
                }
            } catch (error) {
                console.error('Failed to refresh user data:', error);
            }
        },
        
        // Check if user can perform action
        can(action, resource = null) {
            // Admin can do everything
            if (this.isAdmin) return true;
            
            // Check specific permissions
            if (this.hasPermission(`${action}_${resource}`)) return true;
            if (this.hasPermission(`${action}_all`)) return true;
            
            return false;
        },
        
    }
})
